import {
  ListProductAccountingsForAccountApiArg,
  useListProductAccountingsForAccountQuery,
} from "@generated";
import { RootState } from "@reducers/rootReducer";
import { useSelector } from "react-redux";
import { ProductWithCode } from "../types";
import React from "react";

export interface HookArgsForCodes {
  page?: ListProductAccountingsForAccountApiArg["page"];
  productId: ListProductAccountingsForAccountApiArg["productId"];
}

const useListProductAccounting = ({ page, productId }: HookArgsForCodes) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);

  const { data, error, isLoading, refetch, isFetching } =
    useListProductAccountingsForAccountQuery({
      slug: accountSlug,
      productId,
      page,
      include: ["service-category"],
    });

  const productWithCode: ProductWithCode[] = React.useMemo(() => {
    if (!data || !data.data) return [];

    return data.data.map((item) => {
      const id = item?.id || 0;
      const code = item.attributes?.code ?? "";

      const serviceCategoryId =
        item.relationships?.["service-category"]?.data?.id;

      const category =
        serviceCategoryId && data.included
          ? data.included
              .filter(
                (inc) =>
                  inc.type === "service-categories" &&
                  inc.id === serviceCategoryId
              )
              .map((inc) => ({
                id: Number(inc.id) || 0,
                type: inc.type,
                attributes: {
                  nestedName: inc.attributes?.["nested-name"] ?? "",
                },
              }))
          : [];

      return {
        id,
        type: "product-accountings",
        acctCode: [
          {
            id,
            type: "product-accountings",
            attributes: { code },
            relationships: { product: null, serviceCategory: null },
          },
        ],
        category,
        serviceCategoryId: serviceCategoryId
          ? Number(serviceCategoryId)
          : undefined,
      };
    });
  }, [data]);

  const pageMeta = React.useMemo(() => {
    if (!data?.meta) return { recordCount: 0, pageCount: 0 };

    return {
      recordCount: data.meta["record-count"] ?? 0,
      pageCount: data.meta["page-count"] ?? 0,
    };
  }, [data]);

  return {
    productWithCode,
    error,
    acctCodesLoading: isLoading,
    pageMeta,
    refetchAcctCodes: refetch,
    acctCodesFetching: isFetching,
  };
};

export default useListProductAccounting;
